/**
 * The words on the account screen: tab labels, the names of organisation
 * kinds and seniority levels, and every sentence the panels print.
 *
 * Which tabs a subject gets is decided in `profile.model.ts`; this file only
 * knows what each is called. Two things here look like they could be shared
 * and deliberately are not: `IDENTITY_CHECKS` repeats signup's
 * `IDENTITY_REQUIREMENTS` because one is a promise before an account exists
 * and the other a status after, and `ORG_TYPE_TAGS` shadows `ORG_TYPE_LABELS`
 * because a rail pill and a sentence need different lengths of the same name.
 */

import { ORG_PRIVILEGES } from "@ondo/shared";
import type {
  AffiliationAudience,
  AffiliationStanding,
  MembershipLevel,
  OrgPrivilege,
  OrgRole,
  OrgType,
} from "@ondo/shared";

/**
 * The tabs, keyed rather than indexed.
 *
 * Which tabs exist depends on whose profile it is, so the list is built in the
 * model and these are only the labels. See `tabsFor` in profile.model.ts.
 */
export const PROFILE_TABS = {
  profile: "Profile",
  entities: "Entities & Holdings",
  organisation: "Organisation",
  team: "Team",
  account: "Your account",
} as const;

export type ProfileTabKey = keyof typeof PROFILE_TABS;

/** How an organisation's kind reads on screen. */
export const ORG_TYPE_LABELS: Record<OrgType, string> = {
  gallery: "Gallery",
  estate: "Artist's Estate",
  institution: "Institution",
  auction: "Auction House",
  conservator: "Conservator",
  expert: "Expert",
  // The firm an agent works for. Sentence case here like every other label —
  // the design's small-caps "ADVISORY" pill is a styling decision, and a label
  // that carried its own capitalisation could not be reused in a sentence.
  advisory: "Art Advisory",
  registry: "Registry",
  // OND-52's three. A LEGAL OWNER rather than a role somebody works in, which
  // is why none of them opens a workspace -- see roleForOrgType in the API.
  company: "Company",
  trust: "Trust",
  foundation: "Foundation",
};

/**
 * The same kinds, short enough for a pill in a 220px rail.
 *
 * A SECOND MAP, and the comment above predicted it: "the design's small-caps
 * 'ADVISORY' pill is a styling decision, and a label that carried its own
 * capitalisation could not be reused in a sentence." The styling decision has
 * arrived, and it turns out to be a width decision too — "ART ADVISORY" beside
 * "Verified ✓" does not fit the rail's card, and "ARTIST'S ESTATE" fits less.
 *
 * Two maps rather than a `.split(" ").pop()` on the first, which would render
 * "HOUSE" for an auction house. Kept in this file, immediately below the map it
 * shadows, because that is the only thing that makes a drift visible: a new
 * org_type is a compile error in BOTH, and anybody adding a label here sees the
 * tag it needs on the next screen of the same file.
 *
 * Sentence case here as well. The uppercase is `text-transform` in shell.css,
 * for the reason the labels are not capitalised: the words are the data and the
 * case is the design.
 */
export const ORG_TYPE_TAGS: Record<OrgType, string> = {
  gallery: "Gallery",
  estate: "Estate",
  institution: "Institution",
  auction: "Auction",
  conservator: "Conservator",
  expert: "Expert",
  advisory: "Advisory",
  registry: "Registry",
  company: "Company",
  trust: "Trust",
  foundation: "Foundation",
};

/**
 * Seniority, in words.
 *
 * Shown to every member and not only to admins — see the header of
 * `ondo.org_members()`. A registry where only admins can see who holds admin is
 * one where nobody but an admin can notice that the wrong person does.
 */
export const MEMBERSHIP_LEVEL_LABELS: Record<MembershipLevel, string> = {
  admin: "Admin",
  editor: "Editor",
  viewer: "Viewer",
};

/**
 * The four named roles, in the words the roster shows — OND-61.
 *
 * "Authentication Specialist" in full, because that is the job title the
 * ticket's cast carries and the shortening ("Specialist") is the one word in
 * the set that says nothing on its own. The enum value stays `specialist`; a
 * label is not an identifier.
 */
export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  director: "Director",
  registrar: "Registrar",
  scholar: "Scholar",
  specialist: "Authentication Specialist",
};

/**
 * What each privilege lets a seat DO, said as an ability rather than as a
 * permission name.
 *
 * "Read the collector list" and not "collector_list_read": the roster is read
 * by a studio director deciding what their registrar should be able to do, and
 * an enum value is a sentence written for a database.
 *
 * `history_confirm` is the one worth reading twice. It is deliberately NOT
 * "Confirm an attribution", though that is what the ticket's Authentication
 * Specialist does with it, because the privilege is about the ACT and not the
 * kind of history being confirmed — the same reason the database calls it
 * `history_confirm`. A label naming attribution would be the first place
 * somebody concluded there was a second privilege for sales.
 */
export const ORG_PRIVILEGE_LABELS: Record<OrgPrivilege, string> = {
  collector_list_read: "Read the collector list",
  history_confirm: "Confirm or dispute a work's history, in the firm's name",
  share_code_issue: "Issue and revoke share codes",
  staff_admin: "Administer staff, roles and abilities",
};

/**
 * The abilities as a list, in the order the enum declares them.
 *
 * A constant and not a computation, so a view may read it: `ORG_PRIVILEGES`
 * paired with its label is static data about the vocabulary, and building it in
 * a controller would put four lines of `.map` in every screen that draws it.
 */
export const ORG_PRIVILEGE_CHOICES: readonly { privilege: OrgPrivilege; label: string }[] =
  ORG_PRIVILEGES.map((privilege) => ({ privilege, label: ORG_PRIVILEGE_LABELS[privilege] }));

/**
 * The three answers a per-seat ability can have, as a control's options.
 *
 * THREE AND NOT TWO, which is the whole shape of `ondo.membership_privileges`
 * reaching a screen: granted, refused, and NO LIVE ROW — "follow the role",
 * which is the state almost every seat should be in and which a checkbox could
 * not return to once anybody had pressed it.
 *
 * `""` is the cleared value because an HTML select option carries a string and
 * `null` is not one; the controller maps it back.
 */
export const SEAT_ABILITY_OPTIONS: readonly { value: string; label: string }[] = [
  { value: "", label: "Follow the role" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

/**
 * The identity documents ONDO will ask for, and does not hold.
 *
 * THIS LIST IS A PLACEHOLDER AND MUST READ AS ONE. There is no KYC table, no
 * tier, no document store and no provider: signup's `IdentityPanel` offers only
 * "Skip for now", `identitySkipped` is hard-coded true, and the single fact the
 * registry actually holds about identity is `principals.verified`, a boolean
 * ONDO sets by hand.
 *
 * The four rows are here because the shape is the specification — these are the
 * checks the product intends to run, and naming them is more useful than an
 * empty panel. What must never happen is one of them rendering as PASSED. The
 * copy below carries that weight, and `profile.css` gives every row the dashed
 * outline `.tag--unverified` uses, which is the system's existing way of saying
 * "an outline nobody has filled in yet".
 *
 * Same list, same wording as `IDENTITY_REQUIREMENTS` in signup.constants.ts.
 * Not shared with it: that one is a promise made before an account exists and
 * this one is a status afterwards, and they will diverge the moment either side
 * becomes real.
 */
export const IDENTITY_CHECKS = [
  { id: "passport", label: "Passport / National ID" },
  { id: "address", label: "Proof of Address" },
  { id: "aml", label: "AML Screening" },
  { id: "source", label: "Source of Funds Declaration" },
] as const;

/**
 * How many lines of history the profile shows.
 *
 * Shorter than the dashboard's, because this panel sits under eight others and
 * is not what the screen is for. The audit log destination is where the whole
 * trail belongs, and it is still marked planned.
 */
export const ACTIVITY_ROW_LIMIT = 8;

/**
 * Everything the screen says, in one place.
 *
 * The `notYet` entries follow `NOT_YET_COPY` in collector.constants.ts: name
 * the missing piece rather than saying "coming soon", because a reader told
 * "the registry holds no address" understands the product, and one told
 * "coming soon" learns nothing and suspects a bug.
 */
export const PROFILE_COPY = {
  signOut: "Log out",
  edit: "Edit",
  cancel: "Cancel",
  save: "Save changes",
  saving: "Saving…",
  saved: "Saved.",
  loading: "Loading your account…",
  unknownError: "Something went wrong. Please try again.",

  memberSince: "Member since",
  ondoId: "ONDO ID",
  publicAddress: "Public address",
  noHandle: "Not claimed yet",
  verified: "Verified",
  unverified: "Not yet verified",

  membershipHeading: "Membership type",
  /**
   * Several at once is the normal case, not an edge one — a gallery director
   * also collects. The prototype's single-selection grid says otherwise and is
   * wrong; see domain/membership.ts.
   */
  membershipNote:
    "What you told us you are, when you applied. Several can be true at once, and none of them grants anything on its own.",
  /**
   * ADD, NEVER REMOVE, and the sentence says both halves.
   *
   * It read "To change this, write to support@ondo.art" while `declared_types`
   * was write-once at provisioning — true then, and it left an account that
   * arrived by invitation permanently unable to become anything. Adding is now
   * self-service, because a workspace is a route and not a permission; removing
   * still is not, because `collector` is the sole route to the collector
   * capacity and unticking it would strand a holding behind a checkbox.
   */
  membershipChange:
    "You can add to this at any time. To remove one, write to support@ondo.art.",
  membershipAdd: "Add",
  membershipAddNote: "Anything you add opens its workspace. It grants nothing.",
  membershipBusy: "Adding…",
  membershipFailed: "That could not be added. Try again.",
  /**
   * NOT "you did not choose", which was written when everybody chose.
   *
   * Somebody asked by email to confirm one provenance line is asked nothing
   * about themselves, so the empty state is now the normal state for a whole
   * kind of account rather than an oversight to point at.
   */
  membershipNone: "You have not said what you are on ONDO.",

  contactHeading: "Contact",
  contactNote: "What ONDO holds, and what you can correct here.",
  emailNote: "Your email is your sign-in. Change it from your account provider, not here.",
  nameLabel: "Display name",
  emailLabel: "Email",
  phoneLabel: "Phone",
  countryLabel: "Country",
  notGiven: "Not given",

  identityHeading: "Identity verification",
  /**
   * OND-75. "KYC / AML" before, which is an acronym pair that reads as a product
   * somebody has bought.
   *
   * The panel under it already says in full that no provider is connected and
   * nothing has been started, so this was never the dangerous mislabel either.
   * Spelled out it describes the checks the product INTENDS rather than naming a
   * compliance apparatus, which is the distinction the whole panel turns on.
   */
  identitySub: "Identity and anti-money-laundering checks",
  identityNotStarted: "Not started",
  identityNotYet: {
    lead: "Identity checks are not running yet.",
    detail:
      "No verification provider is connected, so none of the checks below has been started and none of them has passed. The one thing the registry does hold is whether ONDO has verified this account by hand, shown above. When a provider is connected these four rows will carry real dates.",
  },

  addressHeading: "Verified address",
  addressNotYet: {
    lead: "No address is held.",
    detail:
      "The registry stores a country and nothing else — there is no address on an ONDO account, verified or otherwise, and no proof-of-address document behind one. An address becomes a record when identity checks do.",
  },

  preferencesHeading: "Preferences",
  /**
   * TRIMMED BY 20261003091000, which built one of the three things this panel
   * used to say did not exist.
   *
   * It read "Notifications are sent for everything addressed to you" — and that
   * sentence is now false: notification delivery is a per-kind setting with its
   * own screen, reachable from the rail. The two that remain are still true, so
   * the panel stays and points at the screen rather than disappearing, which is
   * also how somebody who came here looking for it finds it.
   */
  preferencesNotYet: {
    lead: "Currency and language are not settings yet.",
    detail:
      "Both are shown in the prototype and neither exists: amounts are shown in the currency they were recorded in. Notification settings DO exist — how each kind of notification reaches you, immediately or in the daily digest or not at all, is on the Notification Settings screen in the sidebar.",
  },

  affiliationsHeading: "Affiliations",
  affiliationsNote:
    "Who you have said you are with, and what they answered. A confirmed affiliation grants nothing — it is a fact somebody was willing to stand behind, in the way an attestation on a chain of title is.",
  affiliationsEmpty: "You have not claimed an affiliation.",
  affiliationWithdraw: "Withdraw",
  affiliationAdd: "Claim an affiliation",
  affiliationIdLabel: "Their ONDO ID",
  affiliationTitleLabel: "Your title there",
  affiliationNoteLabel: "Anything they should know",
  affiliationSubmit: "Send claim",
  affiliationIdInvalid: "That is not an ONDO id.",
  affiliationPending: "Awaiting an answer",
  affiliationVisibility: "Who can see this",
  affiliationVisibilityNote:
    "Confirming and showing are separate choices. Changing who can see a relationship does not change what the other party answered, and their answer is always shown beside it.",
  affiliationHidden: "Hidden from everyone",
  affiliationAnswerPrefix: "They said",
  affiliationAnsweredBy: "Answered by",
  affiliationNoAuthority:
    "Showing a relationship gives nobody any authority over a work. Permission to act on a particular work comes from the appointment for that work and the scope it was given.",

  summaryHeading: "Collection summary",
  /**
   * Three of the prototype's four rows are gone. Estimated value needs a
   * valuation the registry does not compute, transfers need `ondo.transfers`
   * (accessFGARules, Deliberately absent), and fees need a ledger. A zero in
   * any of those places would be a claim, not a blank.
   */
  summaryWorks: "Works registered",
  summaryCollections: "Collections",
  summaryRooms: "Viewing rooms",

  /**
   * The estate's three figures. OND-80.
   *
   * "Attested by this estate" rather than "Attested", and the qualifier is the
   * whole of the ticket's one caveat: the count is works this reader's own
   * principal has put a live `verify` on, not works attested by anybody. The
   * unqualified word would be a bigger claim than the number supports, on the
   * screen most likely to be screenshotted for somebody else's due diligence.
   */
  estateWorks: "Works in the catalogue",
  estateAttested: "Attested by this estate",
  estateTeam: "Team members",
  estateHeading: "The catalogue raisonné",

  /**
   * Ending a seat. OND-91.
   *
   * TWO PRESSES, the viewing rooms' own pattern, and the second one names what
   * it does rather than saying "Confirm". A seat IS the firm's access — every
   * relation is reached through an active membership — so the press closes the
   * catalogue, the collector list, the inventory and the signing door in one
   * statement, and the reader should be told that before the second press rather
   * than after it.
   *
   * "End their seat" rather than "Remove": nothing is deleted. The membership row
   * is stamped and kept, because it is what authorised everything that person
   * did, and a word promising removal would be wrong about the record.
   */
  teamEnd: "End their seat",
  teamEndArmed: "Yes — end it now",
  teamEndCancel: "Keep it",
  teamEndWarning:
    "This closes everything the firm reaches for them — the catalogue, the collector list, the inventory, and signing on the firm's behalf. Their seat is kept on the record as ended, with today's date, because it is what authorised what they did.",
  teamEndLast:
    "You are the firm's only administrator. Appoint another before ending your own seat — nobody could administer the firm afterwards, and there is no way to put that right from inside the product.",

  activityHeading: "Account activity",
  activityEmpty: "Nothing has been recorded on this account yet.",

  entitiesHeading: "Organisations",
  entitiesNote:
    "Entities you belong to, and on what terms. Each opens its own workspace — your access there comes from the membership, never from this page.",
  entitiesEmpty: "You do not belong to an organisation on ONDO.",
  holdingsHeading: "Collections",
  holdingsEmpty: "You do not hold a collection yet.",

  orgHeading: "Organisation",
  orgRegisteredAddress: "Registered address",
  orgRegistrationNumber: "Registration number",
  orgType: "Kind",
  orgNotAdmin: "Only an admin of this organisation can change its record.",

  teamHeading: "Team",
  teamNote:
    "Everyone with an active membership, the role each of them holds, and what that role lets them do here.",
  teamJoined: "Joined",
  /**
   * "Job title", WHERE THIS SAID "Role" UNTIL OND-61.
   *
   * The word moved, and it had to. `OrgMember.title` is free text that enforces
   * nothing — "Chief Catalogue Scholar" is whatever somebody typed — and
   * `OrgMember.role` is the bundle the product now enforces. Leaving both under
   * one heading would have put the field that grants nothing beside the field
   * that grants everything and called them the same thing, on the screen whose
   * whole subject is who may do what.
   *
   * That is the ticket's own complaint, read from the other end: "The job titles
   * on the roster are display text; the rung is the only thing the product
   * actually enforces."
   */
  teamTitle: "Job title",
  teamRole: "Role",
  teamNoRole: "No named role",
  teamLevel: "Access",
  teamAbilities: "Can do",
  teamNoAbilities: "Nothing beyond reading this firm's records",
  /**
   * Said of a seat whose abilities are not what its role name would give.
   *
   * It names the fact and passes no judgement — a customised seat is the
   * product working as the owner asked ("editable defaults, not fixed
   * restrictions"), not an anomaly. What it is for is an administrator
   * auditing a roster and needing to know which rows were decided specially.
   */
  teamCustomised: "Set for this seat",
  teamRoleLegend: "What each role can do here",
  /**
   * The sentence under the customisation panel, and it is the one place the
   * product says out loud that these bundles are provisional.
   *
   * It is here rather than in a tooltip because the owner's instruction is
   * explicit that they "can be changed", and a firm editing them should know it
   * is editing a default somebody chose rather than a rule ONDO enforces.
   */
  teamRoleLegendNote:
    "These are ONDO's starting defaults, and your organisation can change them. A tick you set yourself is marked; everything else is the default. Changing a role here changes it for everyone who holds it.",
  teamRoleSet: "Role",
  teamRoleNotAdmin:
    "Only an administrator with staff administration can change roles or abilities here.",
  /**
   * Shown when the database refuses a grant below a privilege's floor.
   *
   * The server's own sentence is shown instead wherever it arrives — it names
   * the rung — and this is the fallback. Both say the same thing: the rung is
   * the coarse mechanism and an ability cannot be given to a seat the rung
   * excludes.
   */
  teamFloorRefused:
    "That ability needs a more senior seat. Raise the seat's access level first.",
  /**
   * ── THE PANEL THAT USED TO SAY THIS WAS NOT BUILT — OND-24 ──
   *
   * It said, until this ticket: "Inviting somebody is not built. A membership is
   * a grant: it decides what its holder can read across the organisation's whole
   * holding. Nothing in ONDO mints one from a form — the same line
   * ondo.affiliation_claims draws when it refuses to write a membership from a
   * confirmed claim. Adding a member goes through ONDO for now."
   *
   * Every word of that was true and the last sentence is what OND-24 removes.
   * The line it drew is not relaxed: `ondo.affiliation_claims` still refuses to
   * write a membership from a confirmed claim, because a claim is somebody
   * saying they work somewhere and a seat is the firm saying so. What is built
   * here is the firm saying so — at the `staff_admin` door, to an address, with
   * the colleague's own acceptance in between. A form still mints nothing.
   *
   * THE COPY SAYS WHAT ACCEPTING DOES, not what inviting does, because that is
   * the fact an administrator is most likely to get wrong: nothing happens to the
   * invited address until the person signs in as themselves and presses accept.
   */
  teamInviteHeading: "Inviting a colleague",
  teamInviteNote:
    "An invitation goes to an email address once. Nothing is created for that address and nobody joins this organisation until they sign in as themselves and accept it — so an invitation to the wrong address does nothing at all. You can take an unanswered invitation back.",
  inviteEmail: "Email address",
  inviteLevel: "Access",
  inviteTitle: "Job title",
  inviteTitleHint: "Optional. Display only — it grants nothing.",
  /**
   * The sentence beside the Access field, and it is the one warning on this
   * panel.
   *
   * ADMIN IS OFFERED, and `20261014092200` decision 4 is why it must be: nothing
   * in ONDO changes a seat's rung once held, so a firm that could not invite an
   * administrator could never gain a second one and its first could never leave.
   * The copy says what that means in the plainest terms available rather than
   * hiding the option.
   */
  inviteLevelNote:
    "An administrator can invite and remove colleagues, which includes you. A seat's access cannot be changed afterwards.",
  inviteSend: "Send invitation",
  inviteSending: "Sending…",
  inviteCancel: "Cancel",
  inviteStart: "Invite a colleague",
  inviteNeedsEmail: "An invitation needs an email address.",
  inviteBadEmail: "That does not look like an email address.",
  /** The firm's own list of what it has sent. */
  inviteSentHeading: "Invitations sent",
  inviteNone: "No invitations have been sent from here.",
  inviteWithdraw: "Withdraw",
  inviteBy: "Invited by",
  /**
   * The five states of an invitation, in one sentence each.
   *
   * `inviteNotDelivered` IS DELIBERATELY VAGUE about the cause, and that is the
   * honest thing rather than a cop-out. A null `deliveredAt` means three
   * different things — no worker has polled yet, the send failed, or the address
   * is on the operator-side suppression list, which records nothing at all on
   * purpose — and the screen cannot tell which. Naming a bounce would be the
   * screen inventing a fact the database declined to record.
   */
  invitePending: "Not sent yet",
  inviteDelivered: "Delivered, waiting for an answer",
  inviteNotDelivered: "Not delivered yet",
  inviteAccepted: "Accepted",
  inviteDeclined: "Declined",
  inviteWithdrawn: "Withdrawn",

  accountHeading: "Your account",
  accountNote:
    "The person signed in, as opposed to the organisation whose workspace this is. The same record wherever you are.",
} as const;

/** Header title and subtitle, by whose profile it is. */
export const PROFILE_HEADERS = {
  person: {
    title: "My Profile",
    subtitle: "Account, identity verification and membership",
  },
  organisation: {
    title: "Profile",
    subtitle: "The organisation's record, and the people inside it",
  },
} as const;

/**
 * The tab/panel pair, as ids rather than as a convention.
 *
 * Stable strings and not `useId`, because the panel is rendered by the screen
 * and the tabs by a child: both have to arrive at the same name without one
 * passing it to the other. Same shape as `work-tab-${id}` on the work record.
 */
export const TAB_PANEL_ID = "profile-panel";

/**
 * The three audiences a relationship can be shown to, widest first.
 *
 * WIDEST FIRST AND NESTED, so the checkboxes read as a sequence rather than as
 * three unrelated switches: turning on a wider audience turns on the narrower
 * ones, which is what `normaliseAudiences` does before the request leaves.
 *
 * "Everyone" DOES NOT MEAN A PUBLIC PAGE, and the label says so in the second
 * sentence rather than leaving a reader to assume it. Nothing on ONDO is
 * readable without signing in, and this setting creates no such surface — it is
 * a ceiling on disclosure, not a door. Getting that wrong in the copy would be
 * the worst failure in this panel, because somebody would choose it believing
 * it published their working life to the internet.
 */
export const AFFILIATION_AUDIENCE_LABELS: Record<AffiliationAudience, string> = {
  everyone: "Everyone",
  ondo_users: "People on ONDO",
  dealing_parties: "People already dealing with you",
};

export const AFFILIATION_AUDIENCE_NOTES: Record<AffiliationAudience, string> = {
  everyone:
    "Anyone ONDO ever permits to see relationship information. Today that is everyone signed in who already has your ONDO ID — ONDO has no public directory and no way to search for a person.",
  ondo_users: "Anyone signed in to ONDO who already has your ONDO ID.",
  dealing_parties:
    "Only parties you have a record with — a consignment they accepted, a sale that completed, a loan they agreed, an appointment, or a collection you shared with them.",
};

/**
 * What a relationship's record says, in one word each.
 *
 * NO ENTRY HERE READS AS A CONFIRMATION EXCEPT `confirmed`, and that is the
 * point of the map rather than a property of it. The standing comes from
 * `affiliationStanding()` in `@ondo/shared`, which has no value meaning "near
 * enough" — so this file cannot invent one by being careless, and an unanswered
 * or disputed relationship cannot be drawn as though the other party had agreed.
 */
export const AFFILIATION_STANDING_LABELS: Record<AffiliationStanding, string> = {
  confirmed: "Confirmed",
  disputed: "Disputed",
  not_recalled: "They don't recall",
  declined: "Declined to answer",
  unanswered: "Awaiting an answer",
  withdrawn: "Withdrawn",
  expired: "Expired",
};
